<template>
  <v-flex xs12>
    <!-- fecha -->
    <v-flex xs12>
      <date-input
        :loading="loadingWorkingHours"
        class="flex"
        :label="$t('fields.date')"
        :errorMessages="state.formErrors ? state.formErrors.date : null"
        :hint="
          !validBookings
            ? $t('activity_reservation.warning.no_checkin_or_checkout')
            : null
        "
        :disabled="!validBookings"
        persistent-hint
        @change="v => (date = v)"
        :rules="[rules.required]"
        :initialValue="date"
        :allowedDates="val => allowedDates(val)"
        required
      />
    </v-flex>
    <!-- time slot -->
    <v-flex xs12 :key="availableSlotsForSelectedDate.length">
      <app-dropdown
        :loading="loadingWorkingHours"
        :disabled="!date || !availableSlotsForSelectedDate.length"
        :label="$t('fields.time')"
        :items="date ? availableSlotsForSelectedDate : []"
        class="notranslate required"
        filled
        :rules="[rules.required]"
        itemValue="hour"
        itemText="hour"
        itemDisabled="disabled"
        :value="slot"
        @change="value => (slot = value)"
        prependInnerIcon="mdi-clock-outline"
        hasSelectionSlot
        :hasItemSlot="false"
        :hint="hintSlotHours"
        :persistentHint="hintSlotHours ? true : false"
      >
        <template v-slot:selection="{ item }">
          {{ item.hour }}
        </template>
        <template v-slot:item="{ item }">
          <v-layout space-between :class="item.class">
            {{ item.hour }}
            <v-spacer />
            {{
              item.reachedMaxCapacity
                ? $t("activity_reservation.reached_capacity")
                : ""
            }}
          </v-layout>
        </template>
      </app-dropdown>
      <v-tooltip bottom right v-if="slotDisabledWarnings">
        <template v-slot:activator="{ on, attrs }">
          <div class="d-flex align-center justify-end" v-bind="attrs" v-on="on">
            <span
              v-if="reservationMethod.type === reservationTypes.ROOM_SERVICE"
              class="caption"
            >
              {{ $t('activity_reservation.delivery_terms') }}
            </span>
            <span
              v-else-if="reservationMethod.type === reservationTypes.IN_VENUE"
              class="caption"
            >
              {{ $t('activity_reservation.booking_terms') }}
            </span>
            <v-btn icon color="trasnparent">
              <v-icon>
                mdi-information-outline
              </v-icon>
            </v-btn>
          </div>
        </template>
        <div class="mt-5">
          <div v-if="reservationMethod.leewayTimeBeforeActivityServiceStart && leewayTimeBeforeActivityServiceStartInit && reservationMethod.leewayTimeBeforeActivityServiceStart >= 0" class="mb-5">
            {{
              $t(`activity_reservation.leeway_time_before_activity_service_start_init.${reservationMethod.type}`)
            }}
          </div>
          <div v-if="reservationMethod.leewayTimeBeforeActivityServiceStart && !leewayTimeBeforeActivityServiceStartInit && reservationMethod.leewayTimeBeforeActivityServiceStart >= 0" class="mb-5">
            {{
              $t(`activity_reservation.leeway_time_before_activity_service_start.${reservationMethod.type}`, {time: formatDuration({ ms: reservationMethod.leewayTimeBeforeActivityServiceStart, extended: false })})
            }}
          </div>
          <div v-if="reservationMethod.minimumTimeNotice && reservationMethod.minimumTimeNotice > 0" class="mb-5">
            {{
              $t(`activity_reservation.slot_notification_min.${reservationMethod.type}`, {
                time: formatDuration({ ms: reservationMethod.minimumTimeNotice, extended: false })
              })
            }}
          </div>
          <div v-if="reservationMethod.maximumTimeNotice" class="mb-5">
            {{
              $t(`activity_reservation.slot_notification_max.${reservationMethod.type}`, {
                time: formatDuration({ ms: reservationMethod.maximumTimeNotice, extended: false })
              })
            }}
          </div>
        </div>
      </v-tooltip>
    </v-flex>
  </v-flex>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import moment from 'moment-timezone'
import { formatTime, formatDuration } from '@/lib/helpers/HoursFormatter'
import { reservationTypes } from '@/config'
import ActivityMixins from '@/mixins/Activity'

export default {
  props: {
    activityId: String,
    reservationMethod: Object,
    initialDate: String,
    initialSlot: Number,
    people: Number,
  },
  data () {
    return {
      loadingWorkingHours: false,
      rules: {
        required: value => !!value || '',
      },
      moment,
      hintSlotHours: null,
      formatDuration,
      leewayTimeBeforeActivityServiceStartInit: false,
      reservationTypes,
    }
  },
  created () {
    this.getWorkingHours()
    if (this.initialSlot) {
      const slotInMillis = moment.duration(this.initialSlot, 'ms')
      this.slot =
        slotInMillis.hours() +
        ':' +
        (slotInMillis.minutes() <= 9 ? '0' : '') +
        slotInMillis.minutes()
    }
    if (this.initialDate) {
      this.date = this.initialDate
    }
  },
  computed: {
    ...mapState({
      state: state => state.state,
      activityReservations: state => state.activityReservations,
    }),
    ...mapGetters({
      availableDates: 'me/availableDates',
      facilityTimeZone: 'me/facilityTimeZone',
      validBookings: 'me/validBookings',
      firstAvailableDate: 'me/firstAvailableDate',
      lastAvailableDate: 'me/lastAvailableDate',
      isDateTimeInSomeBooking: 'me/isDateTimeInSomeBooking',
    }),
    date: {
      get () {
        return this.activityReservations.edited.date
          ? moment(this.activityReservations.edited.date).format('YYYY-MM-DD')
          : null
      },
      set (value) {
        this.$store.commit('activityReservations/setField', {
          field: 'date',
          value,
        })
      },
    },
    slot: {
      get () {
        return this.activityReservations.edited.slot
      },
      set (value) {
        this.$store.commit('activityReservations/setField', {
          field: 'slot',
          value,
        })
      },
    },
    availableSlotsForSelectedDate () {
      return this.getAvailableSlots(this.date)
    },
    slotDisabledWarnings () {
      return this.reservationMethod.minimumTimeNotice > 0 || !!this.reservationMethod.maximumTimeNotice || !!this.reservationMethod.leewayTimeBeforeActivityServiceStart
    },
  },
  methods: {
    allowedDates (val) {
      return this.availableDates.includes(val) && !!this.getAvailableSlots(val).length
    },
    getWorkingHours () {
      this.loadingWorkingHours = true
      return this.$store
        .dispatch('activities/getWorkingHours', {
          id: this.activityId,
          start: this.firstAvailableDate,
          end: this.lastAvailableDate,
          storeSlots: true,
        })
        .then(() => {
          const firstAvailableDate = this.availableDates.find(date =>
            this.allowedDates(date)
          )
          if (!this.date && firstAvailableDate) this.date = firstAvailableDate
        })
        .finally(() => {
          this.loadingWorkingHours = false
        })
    },
    getSlots (date) {
      this.$store
        .dispatch('activities/getSlots', {
          id: this.activityId,
          date,
          save: true,
        })
        .then(() => {
          this.getSlotCapacity({
            slot: this.slot || '00:00',
            activityId: this.activityId,
          }).then(() => {
            const foundSlot = this.availableSlotsForSelectedDate.find(s => {
              return s.hour === this.slot
            })
            if (!foundSlot || foundSlot.disabled) {
              this.slot = null
            }
          })
        })
    },
    getAvailableSlots (date) {
      const slots = this.activities.storedSlots[date] || null
      var availableSlots = []
      if (slots && this.reservationMethod) {
        const {
          minimumTimeNotice,
          maximumTimeNotice,
          reservationTimeInterval,
          attendeeTypeConfiguration,
          leewayTimeBeforeActivityServiceStart,
        } = this.reservationMethod
        var availableHours = slots.availableSlots
        availableHours.forEach(aH => {
          for (
            let time = aH.start;
            time < aH.end;
            time += reservationTimeInterval
          ) {
            if (!slots.excludedSlots.includes(time)) {
              const reachedMaxCapacity =
                attendeeTypeConfiguration !== 'DISABLED' &&
                this.getCapacityStatus({
                  over: true,
                  slot: formatTime(time),
                  curr: this.people || 0,
                })

              const hourRange = moment.tz(date, 'YYYY-MM-DD', this.facilityTimeZone).add(aH.start, 'ms')
              const leewayMoment = moment().add(this.reservationMethod.leewayTimeBeforeActivityServiceStart, 'ms')
              const minimumTimeNoticeMoment = moment().add(minimumTimeNotice, 'ms')
              const maximumTimeNoticeMoment = moment().add(maximumTimeNotice, 'ms')
              const slotMoment = moment.tz(date, 'YYYY-MM-DD', this.facilityTimeZone).add(time, 'ms')
              let data = {
                hour: formatTime(time),
                value: time,
                disabled: reachedMaxCapacity || !this.isDateTimeInSomeBooking(slotMoment),
                notification:
                  reachedMaxCapacity || !this.isDateTimeInSomeBooking(slotMoment)
                    ? this.$t('activity_reservation.fully_booked')
                    : null,
                icon: 'mdi-emoticon-sad-outline',
                reachedMaxCapacity,
              }

              // Add slot to availableSlots
              // If slot is after current moment
              if (slotMoment.isAfter(moment())) {
                // If there is leeway before service start and has already started
                if (leewayTimeBeforeActivityServiceStart !== null && leewayMoment.isAfter(hourRange)) {
                  data.disabled = true
                }
                // If there is max time notice and has passed
                if (maximumTimeNotice && maximumTimeNoticeMoment.isBefore(slotMoment)) {
                  data.disabled = true
                }
                // If there is min time notice
                else if (minimumTimeNotice && minimumTimeNoticeMoment.isAfter(slotMoment)) {
                  data.disabled = true
                }

                if (!data.disabled)
                {
                  availableSlots.push(data)
                }
              }
            }
          }
        })
        availableSlots = availableSlots.sort((a, b) => {
          return moment(a.hour, 'HH:mm').isBefore(moment(b.hour, 'HH:mm'))
            ? -1
            : 1
        })
      }    

      return availableSlots
    },
  },
  watch: {
    date: {
      handler: function () {
        if (this.date) this.getSlots(this.date)
      },
      immediate: true,
    },
    slot () {
      if (this.slot) {
        this.$emit('checkAnotherReservation', { date: this.date, slot: this.slot })
      }
    },
  },
  mixins: [ActivityMixins],
  components: {
    DateInput: () => import('@/components/app-components/DateInput.vue'),
    AppDropdown: () => import('@/components/app-components/AppDropdown.vue'),
  },
}
</script>

<style lang="scss">
.notice {
  padding: 16px 10px;
  background: var(--v-warning-lighten) !important;
  color: var(--v-warning-darken) !important;
}
</style>
